import { useCallback, useMemo } from 'react'
import { uniqBy } from 'lodash'

import { AccountSwitcherAccount as BDSAccountSwitcherAccount } from '@barracuda-internal/bds-core-v2'
import { useAppDispatch, useAppSelector } from 'global/redux/toolkit/hooks'

import useUserDataLib from 'global/lib/userData/useUserData'
import { Account } from 'global/types/api/accountType'
import { AccessToken } from 'global/types/api/accessTokenType'
import * as analyticsLib from 'global/lib/analytics/analyticsService'
import { useFormatMessage } from 'global/lib/localization'
import { config } from 'global/lib/config'
import useDialogLogic from 'global/lib/dialogs/useDialogLogic'
import { Products } from 'global/types/redux/productsType'
import { revertImpersonation } from 'global/redux/features/admin/adminSlice'
import { FEATURES, isMyFeatureOn } from 'global/lib/splitio/splitio'

import { NavbarImpersonationBannerProps } from './navbar'

export interface LayoutLogic {
  accessTokenId?: string
  addMSTenantText?: string
  closeText?: string
  manageAccountText?: string
  isNavBarVisible?: boolean
  accounts: BDSAccountSwitcherAccount[]
  convergedMessageLogLinkLabel: string
  isConvergedMessageLogFeatureOn: boolean
  isDialogOpened: boolean
  isLoggedOut: boolean
  navbarImpersonationBannerProps: NavbarImpersonationBannerProps
  productTitle: string
  sendNavO365TrackingEvent: () => void
  toggleDialog: () => void
}

const BASE_I18N_KEY = 'app.layout'

export default function useLayoutLogic(): [LayoutLogic] {
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const [userDataLib] = useUserDataLib()

  const { accessTokenId, bccAccount, isLoggedOut, isNavBarVisible, splitStore } = useAppSelector(_stores => ({
    accessTokenId: _stores.accessToken.accessToken?.id || '',
    bccAccount: _stores.accessToken.bccAccount,
    isLoggedOut: _stores.app.justLoggedOut,
    isNavBarVisible: _stores.app.activePath.isNavbarVisible,
    splitStore: _stores.splitio
  }))

  const [isDialogOpened, toggleDialog] = useDialogLogic()

  const accounts = userDataLib.getAccounts()

  // BNFIR-4312 hide `Add MS Account` button if the user does not have any accounts licensed for the product
  let showAddMSAccount = true
  if (config.domainConfig.isForensics) {
    showAddMSAccount = !!userDataLib.getAccountsLicensedForProduct('fir').length
  }
  if (config.domainConfig.isSentinel) {
    showAddMSAccount = !!userDataLib.getAccountsLicensedForProduct('sentinel').length
  }

  // Filter out ETS accounts (TODO: support IP account filter rules)
  const formattedAccounts: BDSAccountSwitcherAccount[] = accounts.map((account: Account) => ({
    id: account.bccId,
    name: account.accountName,
    nestedAccounts: account.accessTokens?.reduce(
      (acc: BDSAccountSwitcherAccount['nestedAccounts'], accessToken: AccessToken) => {
        if (accessToken.products.some((product: Products): boolean => product === config.PRODUCTS.ETS)) {
          return acc
        }
        return acc?.concat({
          id: accessToken.id,
          name: accessToken.name
        })
      },
      []
    )
  }))

  const formattedAccountsETS: BDSAccountSwitcherAccount[] = accounts.map((account: Account) => ({
    id: account.bccId,
    name: account.accountName,
    nestedAccounts: uniqBy(
      account.accessTokens
        .filter(({ products }) => products.includes(config.PRODUCTS.ETS))
        .map(({ tenantId, name }) => ({ id: tenantId, name })),
      'id'
    )
  }))

  const navbarImpersonationBannerProps: NavbarImpersonationBannerProps = useMemo(() => {
    return {
      shouldShowBanner: userDataLib.isImpersonationMode(),
      alertContent: formatMessage('impersonation_banner', { user: userDataLib.getUser().email }),
      closeAction: () => {
        const { v2Impersonate } = userDataLib.getUser()
        analyticsLib.trackAppEvent(analyticsLib.EVENTS.NAVBAR_REVERT_IMPERSONATION)
        dispatch(revertImpersonation({ v2Impersonate }))
      },
      closeText: formatMessage('impersonation_banner_button'),
      pageAlert: false,
      showClose: true
    }
  }, [dispatch, formatMessage, userDataLib])

  // Check if ConvergedMessageLog feature is on
  const accountId = useMemo(() => userDataLib.getAccountByAccessToken(accessTokenId)?.accountId, [
    accessTokenId,
    userDataLib
  ])

  // TODO: when we remove the split treament we still need to check if the user has ESS entitlement
  const isConvergedMessageLogFeatureOn = useMemo(() => {
    const isConvergedMessageLogOn = isMyFeatureOn(
      splitStore,
      FEATURES.EGD_IP_IR_CONVERGED_EMAIL_LOG_ROLLOUT,
      bccAccount
    )
    const hasEss = userDataLib.getAccountByAccountId(accountId)?.essEntitled || false

    return isConvergedMessageLogOn && hasEss
  }, [accountId, bccAccount, splitStore, userDataLib])

  const sendNavO365TrackingEvent = useCallback(() => {
    analyticsLib.trackAppEvent(analyticsLib.EVENTS.NAVBAR_CLOUD_CONNECT)
  }, [])

  return useMemo(
    () => [
      {
        accessTokenId,
        accounts: config.domainConfig.isEts ? formattedAccountsETS : formattedAccounts,
        closeText: formatMessage('cancel'),
        addMSTenantText: showAddMSAccount ? formatMessage('add_MS_tenant_text') : undefined,
        manageAccountText: userDataLib.isMspManagedAccount(accessTokenId)
          ? formatMessage('manage_account_text')
          : undefined,
        convergedMessageLogLinkLabel: formatMessage('converged_message_log_link_label'),
        isConvergedMessageLogFeatureOn,
        isLoggedOut,
        isNavBarVisible,
        navbarImpersonationBannerProps,
        productTitle: formatMessage(config.NAME),
        isDialogOpened,
        sendNavO365TrackingEvent,
        toggleDialog
      }
    ],
    [
      accessTokenId,
      formattedAccountsETS,
      formattedAccounts,
      formatMessage,
      showAddMSAccount,
      userDataLib,
      isConvergedMessageLogFeatureOn,
      isLoggedOut,
      isNavBarVisible,
      navbarImpersonationBannerProps,
      isDialogOpened,
      sendNavO365TrackingEvent,
      toggleDialog
    ]
  )
}
